/**
 * Local and advanced panel — local models registered with the router,
 * custom credentials, and diagnostics (operator-only).
 *
 * One engine request: the providers dashboard (shared with Keys and
 * accounts via SWR dedupe — the two panels cost one wire request together).
 * Local runtimes are the groups the engine marks local; custom credentials
 * and deeper diagnostics wait on engine support and are greyed "Coming soon"
 * rather than faked.
 */

import React from 'react';
import {
  getLLMProviderDashboard,
  regionForGroup,
  type LLMProviderCard,
} from '@/api/llmSettings';
import { useSurfaces } from '@/hooks/useSurfaces';
import { ComingSoonBlock, RouterReadError, RouterReadLoading } from './PanelErrorBoundary';
import { useRouterRead } from './useRouterRead';

const DASHBOARD_SWR_KEY = '/api/v1/settings/llm/providers';
const DIAGNOSTICS_SURFACE = 'settings.models.diagnostics';

const LocalRow: React.FC<{ card: LLMProviderCard }> = ({ card }) => (
  <div
    data-testid={`mr-local-row-${card.provider_id}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-space-3)',
      flexWrap: 'wrap',
      border: '1px solid var(--sp-border)',
      borderRadius: 'var(--sp-radius-lg)',
      background: 'var(--sp-bg-elev)',
      padding: '10px var(--sp-space-3)',
    }}
  >
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{card.display_name}</div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
        {card.model_display_name ?? card.model_id ?? card.provider_id}
      </div>
    </div>
    {card.cli_detected ? (
      <span className="mono" style={{ fontSize: 10, color: 'var(--sp-accent)' }}>
        CLI detected
      </span>
    ) : null}
    <span
      className="mono"
      style={{
        fontSize: 10,
        color: card.status === 'ready' ? 'var(--sp-ok)' : card.status === 'unavailable' ? 'var(--sp-error)' : 'var(--sp-text-dim)',
      }}
    >
      {card.status === 'ready' ? 'ready' : card.status === 'unavailable' ? 'unavailable' : 'not configured'}
    </span>
    {card.docs_url ? (
      <a href={card.docs_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--sp-accent)' }}>
        Docs →
      </a>
    ) : null}
  </div>
);

export const LocalAndAdvancedPanel: React.FC = () => {
  const dashboard = useRouterRead(DASHBOARD_SWR_KEY, (signal) => getLLMProviderDashboard({ signal }));
  const surfaces = useSurfaces();

  if (dashboard.error) {
    return <RouterReadError error={dashboard.error} onRetry={dashboard.retry} />;
  }
  if (dashboard.isLoading) {
    return <RouterReadLoading label="Loading local runtimes…" />;
  }

  const groups = dashboard.data?.groups ?? [];
  const localGroups = groups.filter((group) => regionForGroup(group.id, group.label) === 'Local');
  const localCount = localGroups.reduce((sum, group) => sum + group.providers.length, 0);
  const diagnosticsAllowed = surfaces.has(DIAGNOSTICS_SURFACE);

  return (
    <div data-testid="models-routing-local-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-3)' }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
          Local runtimes
        </div>
        {localCount === 0 ? (
          <div className="sp-empty" style={{ padding: '24px 12px' }}>
            The engine detects no local runtimes on this account.
          </div>
        ) : (
          localGroups.map((group) => (
            <React.Fragment key={group.id}>
              {group.providers.map((card) => (
                <LocalRow key={card.provider_id} card={card} />
              ))}
            </React.Fragment>
          ))
        )}
      </section>

      <ComingSoonBlock
        title="Register a local model"
        body="Declaring your own local endpoint to the router lands with the engine's local-model registration read."
        testId="mr-local-register-coming-soon"
      />

      <ComingSoonBlock
        title="Custom credentials"
        body="Arbitrary credential pairs beyond the engine's vault-key providers arrive with engine support."
        testId="mr-custom-credentials-coming-soon"
      />

      {diagnosticsAllowed ? (
        <section
          data-testid="mr-diagnostics-panel"
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
            Diagnostics
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
            providers read: ok · {groups.length} groups · {groups.reduce((sum, group) => sum + group.providers.length, 0)} providers
          </div>
          <ComingSoonBlock
            title="Deep diagnostics"
            body="Router decision traces and per-model callability arrive with the engine's diagnostics read."
            testId="mr-diagnostics-coming-soon"
          />
        </section>
      ) : (
        <div
          data-testid="mr-diagnostics-operator-only"
          aria-disabled="true"
          style={{
            border: '1px dashed var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            padding: 'var(--sp-space-3) var(--sp-space-4)',
            opacity: 0.6,
            fontSize: 12,
            color: 'var(--sp-text-muted)',
          }}
        >
          Diagnostics are operator-only.
        </div>
      )}
    </div>
  );
};
